import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL, VERSION_NODE_COPY_BASE_URL } from "../config";
import { getAuditTrailData } from "../util/getAuditTrailData";
import { getRevAndTraining, prepareCurrentSnapshot, uploadJsonToS3 } from "../util/AppUtils";
import { v4 as uuidv4 } from 'uuid';
import { updateCurrentSnapshot } from "./procedureRevisionSlice";

const baseUrl = BASE_URL + "section-tree";

// 📌 Create Section (POST)
export const createSection = createAsyncThunk(
  "section/create",
  async (
    { sectionData, action_to_show, comment, version_content,allNodesData,partnumber,revision, },
    { dispatch, rejectWithValue }
  ) => {
    // Exclude signature from version_content
    const {item_code_type,status} = JSON.parse(JSON.stringify(version_content))
    const { signature: _, ...sectionDataWithoutSignature } = sectionData || {};
    const { signature: __, ...versionContentWithoutSignature } = version_content || {};
    
    const tree_node_id = uuidv4();
    const created_at = new Date().toISOString();

    const ad = await getAuditTrailData({
      action: "CREATE_SECTION",
      comment: comment,
      version_content: {
        action_to_show,
        ...sectionDataWithoutSignature,
        ...versionContentWithoutSignature,
        tree_node_id
      },
    });
    
    const sectionPayload = {
      created_at,
      ...sectionData,
      audit_trail_data: ad,
    };

    const snapshotPayload = [...allNodesData, {
      tree_node_id,
      steps:[],
      sibling_order:sectionData.sibling_order,
      procedure_tree:{},
      created_at,
      is_deleted:false,
      detail:{
        ...sectionData,
      }
    }];
    
    const previous_content_snapshot = await uploadJsonToS3(allNodesData,partnumber,revision+"_section_previous",true)

    const revAndTrainingData = await getRevAndTraining(dispatch, sectionData.part_required_id_ref)

    const new_content_snapshot = await uploadJsonToS3({
      data:snapshotPayload,
      ...ad.version_content,
      operation: "single_audit_trail",
      ...revAndTrainingData,
    },partnumber,revision+"_section_new",true)

    const payload = { 
      ...sectionPayload,
      audit_trail_data: {
        ...ad,
        previous_content_snapshot,
        new_content_snapshot,
        version_content:{
          action_to_show:ad.version_content.action_to_show,
        }
      },
    };
    try {
      const response = await axios.post(`${baseUrl}/create`, payload);
      
      await dispatch(
        updateCurrentSnapshot({
          partId: sectionData.part_required_id_ref,
          revisionId: sectionData.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating section");
    }
  }
);

// 📌 Update Section (PUT)
export const updateSection = createAsyncThunk(
  "section/update",
  async ({ id, data, allNodesData,partnumber,revision, tree_node_id }, { dispatch, rejectWithValue }) => {
    try {
      const {item_code_type,status} = JSON.parse(JSON.stringify(data.audit_trail_data.version_content))
      const snapshotPayload = allNodesData.map((node)=>{
        if(id===node.section_id_ref){
          return {
            ...node,
            detail:{
              ...node.detail,
              section_name:data.section_name,
              short_description:data.short_description
            }
          }
        }
        return node
      });
      const previous_content_snapshot = await uploadJsonToS3(allNodesData,partnumber,revision+"_section_previous",true)

      const revAndTrainingData = await getRevAndTraining(dispatch, data.part_required_id_ref)

      const new_content_snapshot = await uploadJsonToS3({
        data:snapshotPayload,
        ...data.audit_trail_data.version_content,
        operation: "single_audit_trail",
        tree_node_id,
        ...revAndTrainingData,
      },partnumber,revision+"_section_new",true)

      const updatedData = {
        ...data,
        audit_trail_data: {
          ...data.audit_trail_data,
          previous_content_snapshot,
          new_content_snapshot,
          version_content:{
            action_to_show:data.audit_trail_data.version_content.action_to_show,
          }
        },
      }
      const response = await axios.put(`${baseUrl}/update/${id}`, updatedData);
      
      await dispatch(
        updateCurrentSnapshot({
          partId: data.part_required_id_ref,
          revisionId: data.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating section");
    }
  }
);

// 📌 Get All Sections (GET)
export const getAllSections = createAsyncThunk(
  "section/getAll",
  async ({ limit = 10, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching sections");
    }
  }
);

export const getAllSectionsForPart = createAsyncThunk(
  "section/getAllForPart",
  async ({ partId, revisionId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?part_required_id_ref=${partId}&procedure_rev_ref=${revisionId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching sections");
    }
  }
);

// 📌 Delete Section (DELETE)
export const deleteSection = createAsyncThunk(
  "section/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting section");
    }
  }
);

// 📌 Move Section (POST)
export const moveSection = createAsyncThunk(
  "section/move",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}node-cmd/move`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error moving section");
    }
  }
);

export const moveChildnodes = createAsyncThunk(
  "childnode/move",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}node-cmd/move`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error moving section");
    }
  }
);

// 📌 Copy Elements (POST)
export const copyElement = createAsyncThunk(
  "elements/copy",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}node-cmd/copy-pro-tree-node`,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error copying element");
    }
  }
);

// 📌 Copy Procedure (POST)
export const copyProcedure = createAsyncThunk(
  "elements/copyProcedure",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}node-cmd/copy-pro-tree`,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error copying element");
    }
  }
);

// 📌 Copy Node (version-node-copy/create) - For simple nodes without children (POST)
export const copyNodeVersionHistory = createAsyncThunk(
  "elements/copyNodeVersionHistory",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${VERSION_NODE_COPY_BASE_URL}version-node-copy/create`,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error copying node from version history");
    }
  }
);

// 📌 Copy Version Node (POST) - For simple node copies without children
export const copyVersionNode = createAsyncThunk(
  "elements/copyVersionNode",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${VERSION_NODE_COPY_BASE_URL}version-node-copy/create`,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error copying version node");
    }
  }
);

// 📌 Copy Version Procedure Tree Node (POST) - For complex node copies with children
export const copyVersionProcedureTreeNode = createAsyncThunk(
  "elements/copyVersionProcedureTreeNode",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}version/version-copy-procedure-tree-node`,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error copying procedure tree node");
    }
  }
);

// 📌 Delete Elements (POST)
export const deleteElement = createAsyncThunk(
  "elements/delete",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}node-cmd/delete`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting element");
    }
  }
);

// 🛠 Section Slice
const sectionSlice = createSlice({
  name: "section",
  initialState: {
    sections: [],
    loading: false,
    error: null,
  },
  reducers: {
    resetSections: (state) => {
      state.sections = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSection.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSection.fulfilled, (state, action) => {
        state.loading = false;
        state.sections.push(action.payload);
      })
      .addCase(createSection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(updateSection.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSection.fulfilled, (state, action) => {
        state.loading = false;
        state.sections = state.sections.map((sec) =>
          sec.id === action.payload.id ? action.payload : sec
        );
      })
      .addCase(updateSection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(getAllSections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllSections.fulfilled, (state, action) => {
        state.loading = false;
        state.sections = action.payload.results;
      })
      .addCase(getAllSections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(getAllSectionsForPart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllSectionsForPart.fulfilled, (state, action) => {
        state.loading = false;
        state.sections = action.payload.results;
      })
      .addCase(getAllSectionsForPart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(deleteSection.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSection.fulfilled, (state, action) => {
        state.loading = false;
        state.sections = state.sections.filter(
          (sec) => sec.id !== action.payload
        );
      })
      .addCase(deleteSection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      });
  },
});

export const { resetSections } = sectionSlice.actions;
export default sectionSlice.reducer;
